/**
 * Pricing Legend Configuration
 *
 * Defines the legend configurations for pricing choropleth layers
 */

import type { LegendConfig } from '../AnalysisMap.types'
import {
  PRICING_COLORS,
  PURCHASE_PRICE_RANGES,
  RENTAL_PRICE_RANGES,
} from '../utils/pricingLayerHelpers'

/**
 * Format price values for legend labels
 */
function formatPurchasePrice(value: number): string {
  if (value === 0) return 'CHF 0'
  return new Intl.NumberFormat('de-CH', {
    style: 'currency',
    currency: 'CHF',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value)
}

function formatRentalPrice(value: number): string {
  if (value === 0) return 'CHF 0'
  return new Intl.NumberFormat('de-CH', {
    style: 'currency',
    currency: 'CHF',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value)
}

/**
 * Create legend entries from values and colors
 */
function createLegendEntries(
  values: number[],
  colors: string[],
  formatter: (value: number) => string
) {
  return values.slice(0, -1).map((value, index) => ({
    color: colors[index],
    label: index % 2 === 0 ? `${formatter(value)}` : '',
  }))
}

/**
 * Pricing legend configurations
 */
export const PRICING_LEGEND_CONFIG: Record<string, LegendConfig> = {
  'purchase-market-apartment-price': {
    title: 'Avg Purchase Price/m² - Apartment',
    type: 'gradient',
    unit: 'CHF/m²',
    entries: createLegendEntries(
      PURCHASE_PRICE_RANGES,
      PRICING_COLORS,
      formatPurchasePrice
    ),
  },

  'purchase-market-house-price': {
    title: 'Avg Purchase Price/m² - House',
    type: 'gradient',
    unit: 'CHF/m²',
    entries: createLegendEntries(
      PURCHASE_PRICE_RANGES,
      PRICING_COLORS,
      formatPurchasePrice
    ),
  },

  'rental-market-apartment-price': {
    title: 'Avg Rental Price/m²/mo - Apartment',
    type: 'gradient',
    unit: 'CHF/m²/mo',
    entries: createLegendEntries(
      RENTAL_PRICE_RANGES,
      PRICING_COLORS,
      formatRentalPrice
    ),
  },

  'rental-market-house-price': {
    title: 'Avg Rental Price/m²/mo - House',
    type: 'gradient',
    unit: 'CHF/m²/mo',
    entries: createLegendEntries(
      RENTAL_PRICE_RANGES,
      PRICING_COLORS,
      formatRentalPrice
    ),
  },
}

/**
 * Get legend config for a pricing layer
 */
export function getPricingLegendConfig(
  layerId: string
): LegendConfig | undefined {
  return PRICING_LEGEND_CONFIG[layerId]
}

/**
 * Check if a layer has a pricing legend
 */
export function hasPricingLegend(layerId: string): boolean {
  return layerId in PRICING_LEGEND_CONFIG
}
